import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import OrgHandoffRadial from "./OrgHandoffRadial";
import { orgAgents, orgDivisions, orgRanks, orgModelMeta } from "../assets/files/agentOrg";

// ผังองค์กรแนวตั้ง: ผู้นำทีมอยู่บนสุด → แยกเป็น 4 เลน (ส่วนงาน) → โหนดเรียงลงตามลำดับตำแหน่ง
// โหนดแสดงแค่ไอคอน + ชื่อ รายละเอียดทั้งหมดอยู่ใน modal
// ข้อมูลมาจาก assets/files/agentOrg.js (ถอดจาก .claude/agents)

const ORG_STYLE_ID = "ptn-org-chart-style";
const ORG_CSS = `
.ptn-org { color: #1b3950; }

/* ===== ส่วนหัว ===== */
.ptn-org__hero {
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  padding: 1.3rem 1.6rem;
  color: #ffffff;
  background:
    radial-gradient(900px 320px at 8% -30%, rgba(212, 165, 55, 0.30), transparent 60%),
    radial-gradient(700px 300px at 100% 120%, rgba(86, 204, 242, 0.25), transparent 60%),
    linear-gradient(135deg, #0b2a4a 0%, #0b4f84 58%, #0c76e5 100%);
  box-shadow: 0 18px 38px rgba(11, 42, 74, 0.20);
}
.ptn-org__hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
  background-size: 28px 28px;
  mask-image: linear-gradient(180deg, rgba(0,0,0,0.9), transparent 90%);
  -webkit-mask-image: linear-gradient(180deg, rgba(0,0,0,0.9), transparent 90%);
  pointer-events: none;
}
.ptn-org__hero > * { position: relative; }
.ptn-org__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #f1d58a;
}
.ptn-org__eyebrow::before {
  content: "";
  width: 28px;
  height: 2px;
  border-radius: 2px;
  background: linear-gradient(90deg, #f1d58a, rgba(241, 213, 138, 0));
}
.ptn-org__title { font-size: 1.3rem; font-weight: 800; margin: 0.3rem 0 0.15rem; }
.ptn-org__subtitle { font-size: 0.84rem; color: rgba(255,255,255,0.78); margin: 0; }
.ptn-org__stats { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.ptn-org__stat {
  min-width: 96px;
  padding: 0.45rem 0.85rem;
  border-radius: 14px;
  background: rgba(255,255,255,0.10);
  border: 1px solid rgba(255,255,255,0.22);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
.ptn-org__stat-value { font-size: 1.15rem; font-weight: 800; line-height: 1.1; }
.ptn-org__stat-label { font-size: 0.64rem; letter-spacing: 0.08em; text-transform: uppercase; color: rgba(255,255,255,0.72); }

/* ===== กระดานผัง ===== */
.ptn-org__board {
  margin-top: 1rem;
  border-radius: 22px;
  border: 1px solid rgba(15, 102, 182, 0.16);
  background:
    radial-gradient(circle at 1px 1px, rgba(12, 118, 229, 0.10) 1px, transparent 0) 0 0 / 22px 22px,
    linear-gradient(180deg, #ffffff 0%, #f5f9fe 100%);
  box-shadow: 0 18px 36px rgba(11, 90, 167, 0.10);
  padding: 1.6rem 1.2rem 1.4rem;
}
/* โหมดเส้นทางส่งงาน: พื้นอวกาศมืด */
.ptn-org__board--dark {
  background:
    radial-gradient(900px 500px at 50% 0%, rgba(74, 69, 173, 0.35), transparent 70%),
    radial-gradient(700px 420px at 15% 100%, rgba(12, 118, 229, 0.25), transparent 70%),
    radial-gradient(600px 400px at 90% 90%, rgba(201, 138, 42, 0.18), transparent 70%),
    linear-gradient(180deg, #050d1c 0%, #0a1a33 55%, #0b2a4a 100%);
  border-color: rgba(120, 170, 255, 0.18);
  box-shadow: 0 24px 50px rgba(2, 12, 30, 0.45), inset 0 0 80px rgba(12, 118, 229, 0.08);
  padding: 1rem 1rem 1.2rem;
}
.ptn-org__top { display: flex; justify-content: center; }
.ptn-org__spine { width: 2px; height: 26px; margin: 0 auto; background: rgba(12,118,229,0.55); }

/* เส้นแยกจากผู้นำทีมลงแต่ละเลน (เฉพาะจอกว้าง) */
.ptn-org__rail { position: relative; height: 22px; margin: 0 auto; }
.ptn-org__rail-line {
  position: absolute;
  top: 0;
  height: 2px;
  background: linear-gradient(90deg, rgba(12,118,229,0.2), rgba(12,118,229,0.6) 50%, rgba(12,118,229,0.2));
}
.ptn-org__rail-tick {
  position: absolute;
  top: 0;
  width: 2px;
  height: 22px;
  background: rgba(12,118,229,0.5);
  transform: translateX(-50%);
}

/* ===== เลน (ส่วนงาน) ===== */
.ptn-org__lanes {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
}
.ptn-org__lane {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}
/* เส้นแนวตั้งกลางเลน วิ่งจากหัวเลนถึงกลางโหนดสุดท้าย */
.ptn-org__lane::before {
  content: "";
  position: absolute;
  top: 56px;
  bottom: 26px;
  left: 50%;
  width: 2px;
  background: var(--div-color);
  opacity: 0.35;
  transform: translateX(-50%);
}
.ptn-org__lane > * { position: relative; }

.ptn-org__division {
  width: 100%;
  padding: 0.6rem 0.8rem;
  border-radius: 14px;
  color: #ffffff;
  background: var(--div-gradient);
  box-shadow: 0 10px 22px var(--div-glow);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.ptn-org__division::after {
  content: "";
  position: absolute;
  top: -40%;
  right: -10%;
  width: 46%;
  height: 180%;
  background: radial-gradient(circle, rgba(255,255,255,0.22), transparent 65%);
  pointer-events: none;
}
.ptn-org__division-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.3);
  font-size: 0.85rem;
  flex-shrink: 0;
}
.ptn-org__division-title { font-weight: 800; font-size: 0.84rem; line-height: 1.15; }
.ptn-org__division-sub { font-size: 0.68rem; opacity: 0.85; }

/* ===== โหนด: ไอคอน + ชื่อ ===== */
.ptn-org-node {
  width: min(100%, 220px);
  min-height: 52px;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.7rem 0.45rem 0.5rem;
  border: 1px solid rgba(15, 102, 182, 0.14);
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 6px 14px rgba(11, 90, 167, 0.07);
  color: #0b2a4a;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.ptn-org-node:hover, .ptn-org-node:focus-visible {
  transform: translateY(-2px);
  border-color: var(--div-color);
  box-shadow: 0 14px 26px var(--div-glow);
  outline: none;
}
.ptn-org-node__icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: var(--div-gradient);
  box-shadow: 0 6px 12px var(--div-glow);
  font-size: 0.95rem;
  flex-shrink: 0;
}
.ptn-org-node__name { font-weight: 700; font-size: 0.84rem; line-height: 1.2; }

/* โหนดผู้นำทีม (ทอง) */
.ptn-org-node--lead {
  width: min(100%, 260px);
  min-height: 62px;
  border-color: rgba(184, 138, 32, 0.42);
  background: linear-gradient(135deg, #fffdf5 0%, #fff3d2 48%, #f8e2ac 100%);
  box-shadow: 0 14px 30px rgba(212, 165, 55, 0.30);
  color: #6b4a0c;
  position: relative;
  overflow: hidden;
}
.ptn-org-node--lead:hover, .ptn-org-node--lead:focus-visible {
  border-color: rgba(184, 138, 32, 0.7);
  box-shadow: 0 20px 36px rgba(212, 165, 55, 0.42);
}
.ptn-org-node--lead .ptn-org-node__icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  font-size: 1.1rem;
  background: linear-gradient(135deg, #e3b75a 0%, #b8862c 100%);
  box-shadow: 0 8px 18px rgba(184, 134, 44, 0.42);
  animation: ptnOrgPulse 3.4s ease-in-out infinite;
}
.ptn-org-node--lead .ptn-org-node__name { font-size: 0.98rem; font-weight: 800; }
.ptn-org-node--lead::after {
  content: "";
  position: absolute;
  top: -60%;
  left: -140%;
  width: 50%;
  height: 220%;
  background: linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0) 100%);
  transform: rotate(14deg);
  animation: ptnOrgSheen 8s ease-in-out infinite;
  pointer-events: none;
}

/* สวิตช์โหมด: แผนผังตำแหน่ง / เส้นทางส่งงาน */
.ptn-org__modes {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border-radius: 14px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.24);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
.ptn-org__mode {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.9rem;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: rgba(255,255,255,0.8);
  font-weight: 700;
  font-size: 0.8rem;
  font-family: inherit;
  transition: background 0.2s ease, color 0.2s ease;
}
.ptn-org__mode:hover { color: #ffffff; background: rgba(255,255,255,0.10); }
.ptn-org__mode.is-active { background: #ffffff; color: #0b4f84; box-shadow: 0 6px 14px rgba(4, 24, 44, 0.25); }

.ptn-org__source {
  margin-top: 1.2rem;
  text-align: center;
  font-size: 0.7rem;
  color: #8299ab;
}

/* ===== Modal รายละเอียด ===== */
.ptn-org-modal .modal-content { border: 0; border-radius: 24px; overflow: hidden; box-shadow: 0 30px 60px rgba(11, 42, 74, 0.28); }
.ptn-org-modal__head {
  position: relative;
  overflow: hidden;
  padding: 1.4rem 1.6rem;
  color: #ffffff;
  background: var(--div-gradient);
}
.ptn-org-modal__head::after {
  content: "";
  position: absolute;
  top: -50%;
  right: -10%;
  width: 50%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.25), transparent 65%);
  pointer-events: none;
}
.ptn-org-modal__head > * { position: relative; }
.ptn-org-modal__icon {
  width: 58px;
  height: 58px;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.35);
  font-size: 1.35rem;
  flex-shrink: 0;
}
.ptn-org-modal__eyebrow { font-size: 0.66rem; letter-spacing: 0.18em; text-transform: uppercase; opacity: 0.85; font-weight: 700; }
.ptn-org-modal__title { font-size: 1.3rem; font-weight: 800; line-height: 1.15; }
.ptn-org-modal__th { font-size: 0.86rem; opacity: 0.9; }
.ptn-org-modal__head > .ptn-org-modal__close {
  position: absolute;
  z-index: 3; /* อยู่เหนือแถวชื่อ/ไอคอนที่วาดทีหลัง ไม่งั้นกดโดนแค่ขอบปุ่ม */
  top: 0.9rem;
  right: 0.9rem;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.4);
  background: rgba(255,255,255,0.16);
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}
.ptn-org-modal__head > .ptn-org-modal__close:hover { background: rgba(255,255,255,0.32); transform: scale(1.06); }
.ptn-org-modal__head > .ptn-org-modal__close i { pointer-events: none; }
.ptn-org-modal__body { padding: 1.3rem 1.6rem 1.5rem; background: linear-gradient(180deg, #ffffff 0%, #f5f9fe 100%); }
.ptn-org-modal__summary { font-size: 0.9rem; color: #22384a; line-height: 1.55; margin-bottom: 1rem; }
.ptn-org-modal__label {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #0b4f84;
  margin-bottom: 0.45rem;
  display: flex;
  align-items: center;
  gap: 0.45rem;
}
.ptn-org-modal__section { margin-bottom: 1.1rem; }
.ptn-org-modal__duties { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.45rem; }
.ptn-org-modal__duties li {
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  font-size: 0.86rem;
  color: #22384a;
  line-height: 1.5;
  padding: 0.55rem 0.75rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid rgba(15, 102, 182, 0.10);
}
.ptn-org-modal__duties i { color: var(--div-color); margin-top: 0.22rem; font-size: 0.8rem; }
.ptn-org-modal__note {
  font-size: 0.85rem;
  line-height: 1.5;
  color: #22384a;
  padding: 0.7rem 0.9rem;
  border-radius: 14px;
  background: var(--div-soft);
  border: 1px solid var(--div-color);
  border-left-width: 4px;
}
.ptn-org-modal__boundary {
  font-size: 0.85rem;
  line-height: 1.5;
  color: #7a4a12;
  padding: 0.7rem 0.9rem;
  border-radius: 14px;
  background: #fff7e6;
  border: 1px solid rgba(226, 159, 4, 0.35);
  border-left-width: 4px;
}
.ptn-org-modal__meta { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.6rem; margin-bottom: 1.1rem; }
.ptn-org-modal__meta-box {
  padding: 0.6rem 0.8rem;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(15, 102, 182, 0.12);
}
.ptn-org-modal__meta-label { font-size: 0.64rem; letter-spacing: 0.1em; text-transform: uppercase; color: #8299ab; font-weight: 700; }
.ptn-org-modal__meta-value { font-size: 0.86rem; font-weight: 700; color: #0b2a4a; }
.ptn-org-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.22rem 0.6rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  color: #4f6d83;
  background: #eef4fb;
  border: 1px solid rgba(12, 118, 229, 0.14);
  white-space: nowrap;
}
.ptn-org-chip--model { color: var(--model-color); background: var(--model-bg); border-color: var(--model-border); }

@keyframes ptnOrgSheen { 0% { left: -140%; } 30%, 100% { left: 160%; } }
@keyframes ptnOrgPulse {
  0%, 100% { box-shadow: 0 8px 18px rgba(184, 134, 44, 0.42); }
  50% { box-shadow: 0 12px 26px rgba(212, 165, 55, 0.6); }
}
@media (prefers-reduced-motion: reduce) {
  .ptn-org-node--lead::after, .ptn-org-node--lead .ptn-org-node__icon { animation: none !important; }
}

/* แท็บเล็ต: 2 เลนต่อแถว ไม่มีเส้นแยกแนวนอน */
@media (max-width: 991.98px) {
  .ptn-org__lanes { grid-template-columns: repeat(2, minmax(0, 1fr)); row-gap: 1.4rem; }
  .ptn-org__rail { display: none; }
  .ptn-org__spine { height: 20px; }
}
/* มือถือ: เลนเดียว */
@media (max-width: 575.98px) {
  .ptn-org__hero { padding: 1.1rem 1.1rem; border-radius: 18px; }
  .ptn-org__title { font-size: 1.1rem; }
  .ptn-org__stat { min-width: calc(50% - 0.25rem); }
  .ptn-org__board { padding: 1.2rem 0.8rem 1rem; }
  .ptn-org__lanes { grid-template-columns: 1fr; }
  .ptn-org-modal__meta { grid-template-columns: 1fr 1fr; }
  .ptn-org-modal__head, .ptn-org-modal__body { padding-left: 1.1rem; padding-right: 1.1rem; }
}
`;

if (typeof document !== "undefined" && !document.getElementById(ORG_STYLE_ID)) {
  const styleTag = document.createElement("style");
  styleTag.id = ORG_STYLE_ID;
  styleTag.innerHTML = ORG_CSS;
  document.head.appendChild(styleTag);
}

const LEAD_DIVISION = {
  label: "ทุกส่วนงาน",
  labelTh: "คุมภาพรวมทั้งทีม",
  color: "#b8862c",
  soft: "#fff6dc",
  glow: "rgba(212, 165, 55, 0.35)",
  gradient: "linear-gradient(135deg, #e3b75a 0%, #b8862c 100%)",
};

const divisionVars = (division) => ({
  "--div-color": division.color,
  "--div-soft": division.soft,
  "--div-glow": division.glow,
  "--div-gradient": division.gradient,
});

const rankOrder = orgRanks.reduce((acc, rank, index) => ({ ...acc, [rank.key]: index }), {});

// โหนดในผัง: ไอคอน + ชื่อ เท่านั้น
const OrgNode = ({ agent, division, lead = false, onSelect }) => (
  <button
    type="button"
    className={`ptn-org-node${lead ? " ptn-org-node--lead" : ""}`}
    style={divisionVars(division)}
    title={`${agent.titleTh} · คลิกดูรายละเอียด`}
    onClick={() => onSelect(agent)}
  >
    <span className="ptn-org-node__icon">
      <i className={`fa-solid ${agent.icon}`} />
    </span>
    <span className="ptn-org-node__name">{agent.title}</span>
  </button>
);

const ModelChip = ({ model }) => {
  const meta = orgModelMeta[model] || orgModelMeta.sonnet;
  return (
    <span
      className="ptn-org-chip ptn-org-chip--model"
      title={meta.hint}
      style={{ "--model-color": meta.color, "--model-bg": meta.bg, "--model-border": meta.border }}
    >
      <i className="fa-solid fa-microchip" />
      {meta.label}
    </span>
  );
};

const AgentDetailModal = ({ agent, onHide }) => {
  if (!agent) return null;
  const division = orgDivisions.find((item) => item.key === agent.division) || LEAD_DIVISION;
  const rank = orgRanks.find((item) => item.key === agent.rank);

  return (
    <Modal show onHide={onHide} centered size="lg" dialogClassName="ptn-org-modal" contentClassName="border-0">
      <div className="ptn-org-modal__head" style={divisionVars(division)}>
        <button type="button" className="ptn-org-modal__close" onClick={onHide} aria-label="ปิด">
          <i className="fa-solid fa-xmark" />
        </button>
        <div className="d-flex align-items-center gap-3">
          <span className="ptn-org-modal__icon">
            <i className={`fa-solid ${agent.icon}`} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="ptn-org-modal__eyebrow">
              {division.label} · {rank?.key} {rank?.label}
            </div>
            <div className="ptn-org-modal__title">{agent.title}</div>
            <div className="ptn-org-modal__th">{agent.titleTh}</div>
          </div>
        </div>
      </div>
      <div className="ptn-org-modal__body" style={divisionVars(division)}>
        <p className="ptn-org-modal__summary">{agent.summary}</p>

        <div className="ptn-org-modal__meta">
          <div className="ptn-org-modal__meta-box">
            <div className="ptn-org-modal__meta-label">ส่วนงาน</div>
            <div className="ptn-org-modal__meta-value">{division.labelTh}</div>
          </div>
          <div className="ptn-org-modal__meta-box">
            <div className="ptn-org-modal__meta-label">ระดับ</div>
            <div className="ptn-org-modal__meta-value">
              {rank?.key} · {rank?.labelTh}
            </div>
          </div>
          <div className="ptn-org-modal__meta-box">
            <div className="ptn-org-modal__meta-label">โมเดล</div>
            <div className="ptn-org-modal__meta-value">
              <ModelChip model={agent.model} />
            </div>
          </div>
        </div>

        <div className="ptn-org-modal__section">
          <div className="ptn-org-modal__label">
            <i className="fa-solid fa-list-check" />
            หน้าที่หลัก
          </div>
          <ul className="ptn-org-modal__duties">
            {agent.duties.map((duty, index) => (
              <li key={index}>
                <i className="fa-solid fa-circle-check" />
                <span>{duty}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="ptn-org-modal__section">
          <div className="ptn-org-modal__label">
            <i className="fa-solid fa-bolt" />
            ใช้เมื่อ
          </div>
          <div className="ptn-org-modal__note">{agent.useWhen}</div>
        </div>

        <div className="ptn-org-modal__section">
          <div className="ptn-org-modal__label">
            <i className="fa-solid fa-hand" />
            ขอบเขตที่ไม่ทำ
          </div>
          <div className="ptn-org-modal__boundary">{agent.boundary}</div>
        </div>

        <div className="ptn-org-modal__section mb-0">
          <div className="ptn-org-modal__label">
            <i className="fa-solid fa-screwdriver-wrench" />
            เครื่องมือที่ใช้ได้
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            {agent.tools.map((tool) => (
              <span key={tool} className="ptn-org-chip">
                {tool}
              </span>
            ))}
            <span className="ptn-org-chip ms-auto" style={{ fontWeight: 500 }}>
              .claude/agents/{agent.id}.md
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const chartModes = [
  { key: "tree", label: "แผนผังตำแหน่ง", icon: "fa-sitemap" },
  { key: "flow", label: "เส้นทางส่งงาน", icon: "fa-circle-nodes" },
];

export default function OrgChartMatrix() {
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("tree");

  const lead = useMemo(() => orgAgents.find((agent) => agent.rank === "L1"), []);
  const members = useMemo(() => orgAgents.filter((agent) => agent.rank !== "L1"), []);

  // แต่ละเลน: agent ของส่วนงานนั้น เรียงจากระดับสูงไปต่ำ
  const lanes = useMemo(
    () =>
      orgDivisions.map((division) => ({
        division,
        agents: members
          .filter((agent) => agent.division === division.key)
          .sort((a, b) => rankOrder[a.rank] - rankOrder[b.rank]),
      })),
    [members]
  );

  const modelCounts = useMemo(
    () =>
      orgAgents.reduce((acc, agent) => {
        acc[agent.model] = (acc[agent.model] || 0) + 1;
        return acc;
      }, {}),
    []
  );

  const laneCount = orgDivisions.length;
  const railEdge = `${100 / (laneCount * 2)}%`;

  return (
    <div className="ptn-org">
      <div className="ptn-org__hero">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-end gap-3">
          <div>
            <span className="ptn-org__eyebrow">Organization Chart</span>
            <h4 className="ptn-org__title">ผังองค์กรทีม AI Agent</h4>
            <p className="ptn-org__subtitle">
              {mode === "tree"
                ? "เรียงตามลำดับตำแหน่งในแต่ละส่วนงาน คลิกที่ชื่อเพื่อดูหน้าที่และขอบเขต"
                : "ใครส่งงานให้ใคร: Tech Lead อยู่ศูนย์กลาง เส้นลูกศรคือการส่งงานต่อระหว่างตำแหน่ง"}
            </p>
            <div className="ptn-org__modes mt-3" role="tablist" aria-label="โหมดผังองค์กร">
              {chartModes.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={mode === item.key}
                  className={`ptn-org__mode${mode === item.key ? " is-active" : ""}`}
                  onClick={() => setMode(item.key)}
                >
                  <i className={`fa-solid ${item.icon}`} />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div className="ptn-org__stats">
            <div className="ptn-org__stat">
              <div className="ptn-org__stat-value">{orgAgents.length}</div>
              <div className="ptn-org__stat-label">ตำแหน่ง</div>
            </div>
            <div className="ptn-org__stat">
              <div className="ptn-org__stat-value">{orgDivisions.length}</div>
              <div className="ptn-org__stat-label">ส่วนงาน</div>
            </div>
            <div className="ptn-org__stat">
              <div className="ptn-org__stat-value">{orgRanks.length}</div>
              <div className="ptn-org__stat-label">ระดับ</div>
            </div>
            <div className="ptn-org__stat">
              <div className="ptn-org__stat-value">
                {modelCounts.opus || 0} <span style={{ fontSize: "0.75rem", opacity: 0.7 }}>/ {modelCounts.sonnet || 0}</span>
              </div>
              <div className="ptn-org__stat-label">Opus / Sonnet</div>
            </div>
          </div>
        </div>
      </div>

      <div className={`ptn-org__board${mode === "flow" ? " ptn-org__board--dark" : ""}`}>
        {mode === "flow" ? (
          <OrgHandoffRadial onDetail={setSelected} />
        ) : (
        <>
        {/* ผู้นำทีม */}
        <div className="ptn-org__top">
          <OrgNode agent={lead} division={LEAD_DIVISION} lead onSelect={setSelected} />
        </div>
        <div className="ptn-org__spine" />

        {/* เส้นแยกลงแต่ละเลน */}
        <div className="ptn-org__rail">
          <span className="ptn-org__rail-line" style={{ left: railEdge, right: railEdge }} />
          {orgDivisions.map((division, index) => (
            <span key={division.key} className="ptn-org__rail-tick" style={{ left: `${(100 / laneCount) * (index + 0.5)}%` }} />
          ))}
        </div>

        {/* เลนส่วนงาน */}
        <div className="ptn-org__lanes">
          {lanes.map(({ division, agents }) => (
            <div key={division.key} className="ptn-org__lane" style={divisionVars(division)}>
              <div className="ptn-org__division">
                <span className="ptn-org__division-icon">
                  <i className={`fa-solid ${division.icon}`} />
                </span>
                <span className="d-flex flex-column" style={{ minWidth: 0 }}>
                  <span className="ptn-org__division-title">{division.label}</span>
                  <span className="ptn-org__division-sub">{division.labelTh}</span>
                </span>
              </div>
              {agents.map((agent) => (
                <OrgNode key={agent.id} agent={agent} division={division} onSelect={setSelected} />
              ))}
            </div>
          ))}
        </div>

        <div className="ptn-org__source">
          <i className="fa-solid fa-folder-tree me-1" />
          ที่มา: .claude/agents · เรียงจากระดับ {orgRanks[0].key} ถึง {orgRanks[orgRanks.length - 1].key}
        </div>
        </>
        )}
      </div>

      <AgentDetailModal agent={selected} onHide={() => setSelected(null)} />
    </div>
  );
}
